/* @flow */

import clsx from 'clsx';
import { produce } from 'immer';
import lottie from 'lottie-web';
import * as React from 'react';
import { connect } from 'react-redux';

import type { NETGEM_API_DMS_REQUEST_RESPONSE_CHALLENGE } from '../../libs/netgemLibrary/dms/types/RequestResponseChallenge';
import type { NETGEM_API_V8_REQUEST_RESPONSE } from '../../libs/netgemLibrary/v8/types/RequestResponse';
import type { CombinedReducers } from '../../redux/reducers';
import type { Dispatch } from '../../redux/types/types';

import { getAppLottieLoader } from '../../helpers/applicationCustomization/ui';
import { MILLISECONDS_PER_SECOND } from '../../helpers/dateTime/Format';
import { logDebug, logError, logInfo, logWarning } from '../../helpers/debug/debug';
import HotKeys from '../../helpers/hotKeys/hotKeys';
import Localizer from '../../helpers/localization';
import { isTabHidden } from '../../helpers/ui/browser';
import ReducedMotionManager from '../../helpers/ui/reducedMotionManager';
import { DmsNetworkCode } from '../../libs/netgemLibrary/dms/constants/NetworkCodesAndMessages';
import { CustomNetworkError } from '../../libs/netgemLibrary/helpers/CustomNetworkError';
import { toggleDebugMode } from '../../redux/appConf/actions';
import { RegistrationType } from '../../redux/appRegistration/types/types';
import sendGetDeviceChannelListRequest from '../../redux/netgemApi/actions/dms/channelList';
import sendGetDeviceSettingsRequest from '../../redux/netgemApi/actions/dms/deviceSettings';
import sendLoginRequest from '../../redux/netgemApi/actions/dms/login';
import sendV8DiscoveryRequest from '../../redux/netgemApi/actions/v8/discovery';
import { sendV8ContinuousWatchingHubRequest, sendV8DefaultHubRequest, sendV8HubRequest } from '../../redux/netgemApi/actions/v8/hub';
import { createTagListAliases, sendV8ListAliasPostRequest } from '../../redux/netgemApi/actions/v8/listAlias';
import sendV8MetadataChannelsRequest from '../../redux/netgemApi/actions/v8/metadataChannels';
import { sendCommercialOffersRequest, sendDefaultRightsRequest, sendUserRightsRequest } from '../../redux/netgemApi/actions/v8/rights';
import sendVideofuturAuthenticationRequest from '../../redux/netgemApi/actions/videofutur/authentication';
import sendVideofuturDiscoveryRequest from '../../redux/netgemApi/actions/videofutur/helpers/discovery';
import { stopOpenStreams } from '../../redux/netgemApi/actions/videofutur/helpers/stopOpenStreams';
import sendVideofuturPersonalDataRequest from '../../redux/netgemApi/actions/videofutur/personalData';
import InfiniteCircleLoaderArc from '../loader/infiniteCircleLoaderArc';
import DebugPicto from '../pictos/DebugPicto';
import './StartView.css';
import { filterCurrentSteps, isBOStepEnabled } from './helper';
import {
  type CompleteStartPropType,
  Css,
  Padding,
  QueryStatus,
  type ReduxStartDispatchToPropsType,
  type ReduxStartReducerStateType,
  STARTING_TIMEOUT,
  STEPS,
  type STEP_ITEM,
  type STEP_SET,
  type STEP_TYPE,
  type StartPropType,
  type StartStateType,
  StepName,
} from './StartViewConstsAndTypes';

const formatMs = (time: number) => new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0, style: 'unit', unit: 'millisecond' }).format(time);

const InitialState: Readonly<StartStateType> = Object.freeze({
  currentSteps: [],
  durations: {},
  isDebugPanelVisible: null,
  killedStreamCount: '0',
  nextSteps: [],
  queryStatuses: {
    [StepName.ChannelList]: QueryStatus.NotStarted,
    [StepName.ChannelsInfo]: QueryStatus.NotStarted,
    [StepName.CommercialOffers]: QueryStatus.NotStarted,
    [StepName.ContinuousWatchingHub]: QueryStatus.NotStarted,
    [StepName.DefaultHub]: QueryStatus.NotStarted,
    [StepName.DefaultRights]: QueryStatus.NotStarted,
    [StepName.DeviceSettings]: QueryStatus.NotStarted,
    [StepName.Discovery]: QueryStatus.NotStarted,
    [StepName.Hub]: QueryStatus.NotStarted,
    [StepName.ListAlias]: QueryStatus.NotStarted,
    [StepName.Login]: QueryStatus.NotStarted,
    [StepName.TagListAliases]: QueryStatus.NotStarted,
    [StepName.UserRights]: QueryStatus.NotStarted,
    [StepName.VideofuturAuthentication]: QueryStatus.NotStarted,
    [StepName.VideofuturDiscovery]: QueryStatus.NotStarted,
    [StepName.VideofuturPersonalData]: QueryStatus.NotStarted,
    [StepName.VideofuturStopOpenStreams]: QueryStatus.NotStarted,
  },
  stepProgress: 1,
});

class StartView extends React.PureComponent<CompleteStartPropType, StartStateType> {
  abortController: AbortController;

  animation: any;

  areQueriesFinished: boolean;

  enabledSteps: Set<string>;

  initializationSequenceEnd: number;

  initializationSequenceStart: number;

  isAnimationFinished: boolean;

  mainLayout: HTMLElement | null;

  orderedSteps: Array<STEP_TYPE>;

  rootStep: STEP_ITEM;

  startedSteps: Set<string>;

  startingTimer: TimeoutID | null;

  stepCount: number;

  constructor(props: CompleteStartPropType) {
    super(props);

    const {
      isDebugModeEnabled,
      localCreateTagListAliases,
      localSendCommercialOfferRequest,
      localSendContinuousWatchingHubRequest,
      localSendDefaultHubRequest,
      localSendDefaultRightsRequest,
      localSendDiscoveryRequest,
      localSendGetDeviceChannelListRequest,
      localSendGetDeviceSettingRequest,
      localSendHubRequest,
      localSendListAliasPostRequest,
      localSendMetadataChannelsRequest,
      localSendUserRightsRequest,
      localSendVideofuturAuthenticationRequest,
      localSendVideofuturDiscoveryRequest,
      localSendVideofuturPersonalDataRequest,
      localStopVideofuturOpenStreams,
    } = props;

    this.abortController = new AbortController();
    this.areQueriesFinished = false;
    this.initializationSequenceEnd = 0;
    this.initializationSequenceStart = performance.now();
    this.isAnimationFinished = true;
    this.mainLayout = null;
    this.startedSteps = new Set<string>();
    this.startingTimer = null;

    const { enabled, ordered } = this.prepareSteps(STEPS);
    this.enabledSteps = enabled;
    this.orderedSteps = ordered;
    this.stepCount = this.orderedSteps.length;

    this.rootStep = {
      // DMS Login
      name: StepName.Login,
      request: null,
      then: [
        {
          // DMS Device Settings
          name: StepName.DeviceSettings,
          request: localSendGetDeviceSettingRequest,
          then: [
            {
              // DMS Channel List
              name: StepName.ChannelList,
              request: localSendGetDeviceChannelListRequest,
            },
            {
              // Platform Discovery
              name: StepName.Discovery,
              request: localSendDiscoveryRequest,
            },
            {
              // BO Login
              name: StepName.VideofuturAuthentication,
              parameters: ['applicationId', 'deviceKey'],
              request: localSendVideofuturAuthenticationRequest,
            },
          ],
        },
      ],
      thenAfter: [
        {
          // Platform Channel List Alias: needs channelList & discovery
          name: StepName.ListAlias,
          request: localSendListAliasPostRequest,
          then: [
            {
              // Platform Channels Info: needs List Alias
              name: StepName.ChannelsInfo,
              request: localSendMetadataChannelsRequest,
              then: [
                {
                  // Platform Tag List Aliases: creates one list alias per tag (needs Channels Info)
                  name: StepName.TagListAliases,
                  request: localCreateTagListAliases,
                },
              ],
            },
          ],
        },
        {
          // Platform Default Hub: needs discovery
          name: StepName.DefaultHub,
          request: localSendDefaultHubRequest,
        },
        {
          // Platform Continuous Watching Hub: needs discovery
          name: StepName.ContinuousWatchingHub,
          request: localSendContinuousWatchingHubRequest,
        },
        {
          // BO Discovery: needs BO Auth (only)
          name: StepName.VideofuturDiscovery,
          request: localSendVideofuturDiscoveryRequest,
          then: [
            {
              // BO Personal Data
              name: StepName.VideofuturPersonalData,
              request: localSendVideofuturPersonalDataRequest,
              then: [
                {
                  // Platform Hub: needs discovery & BO Auth
                  name: StepName.Hub,
                  request: localSendHubRequest,
                },
              ],
            },
            {
              // BO Kill Streams
              name: StepName.VideofuturStopOpenStreams,
              request: localStopVideofuturOpenStreams,
            },
          ],
        },
        {
          // Platform Commercial Offers
          name: StepName.CommercialOffers,
          request: localSendCommercialOfferRequest,
        },
        {
          // Platform Default Rights
          name: StepName.DefaultRights,
          request: localSendDefaultRightsRequest,
        },
        {
          // Platform User Rights
          name: StepName.UserRights,
          request: localSendUserRightsRequest,
        },
      ],
    };

    this.state = {
      ...InitialState,
      currentSteps: [this.rootStep],
      isDebugPanelVisible: isDebugModeEnabled ? true : null,
    };
  }

  componentDidMount() {
    const { applicationId, authDeviceUrl, deviceKey, isLoadingAnimationVisible, subscriberId, timeoutCallback, upgradeDeviceUrl } = this.props;

    HotKeys.register(['alt+d', 'ctrl+d', 'shift+d'], this.handleToggleDebugPanelHotKey, { name: 'Start.debug' });

    // Initialize starting sequence by logging into the DMS
    this.sendLoginRequest(authDeviceUrl, upgradeDeviceUrl, applicationId, subscriberId, deviceKey);
    this.startingTimer = setTimeout(timeoutCallback, STARTING_TIMEOUT);

    if (!isLoadingAnimationVisible) {
      // Hide loading animation is this screen is shown more than once
      return;
    }

    const animationData = getAppLottieLoader();

    if (animationData) {
      const isReduceMotionActive = ReducedMotionManager.isReduced();

      this.animation = lottie.loadAnimation({
        animationData,
        autoplay: !isReduceMotionActive,
        container: document.getElementsByClassName('loaderAnimation')[0],
        loop: false,
        renderer: 'svg',
        rendererSettings: {
          progressiveLoad: true,
        },
      });

      if (isReduceMotionActive) {
        // Render immediately at final frame and mark animation as completed
        this.isAnimationFinished = true;

        this.animation.addEventListener('DOMLoaded', () => {
          this.animation.goToAndStop(this.animation.totalFrames - 1, true);
          this.checkInitializationSequenceEnd();
        });
      } else {
        // Run animation and wait until it completes
        this.isAnimationFinished = false;
        this.animation.addEventListener('complete', this.onAnimationComplete, { passive: true });
      }
    }
  }

  componentDidUpdate(prevProps: CompleteStartPropType) {
    const { authenticationToken } = this.props;
    const { authenticationToken: prevAuthenticationToken } = prevProps;

    if (authenticationToken !== prevAuthenticationToken) {
      this.handleTokenUpdate(authenticationToken);
    }
  }

  componentWillUnmount() {
    const { successCallback } = this.props;
    const { abortController, animation, mainLayout } = this;

    this.resetStartingTimer();

    abortController.abort('Component StartView will unmount');

    HotKeys.unregister(['alt+d', 'ctrl+d', 'shift+d'], this.handleToggleDebugPanelHotKey);

    if (mainLayout) {
      mainLayout.removeEventListener('animationend', successCallback, { passive: true });
    }

    if (animation) {
      animation.removeEventListener('complete', this.onAnimationComplete, { passive: true });
      animation.destroy();
    }
  }

  prepareSteps = (steps: Readonly<STEP_SET>): { enabled: Set<string>, ordered: Array<STEP_TYPE> } => {
    const { features } = this.props;

    // Filter out steps linked to disabled features
    const enabled = new Set<string>();
    const ordered: Array<STEP_TYPE> = [];
    Object.keys(steps).forEach((key) => {
      const value = steps[key];

      if (!value) {
        return;
      }

      const { feature, name } = value;

      if ((!feature || features[feature]) && isBOStepEnabled(name)) {
        enabled.add(name);
        ordered.push(value);
      }
    });

    // Sort steps for display
    ordered.sort((a, b) => a.displayIndex - b.displayIndex);

    return {
      enabled,
      ordered,
    };
  };

  handleTokenUpdate = (token: string | null) => {
    if (token) {
      // Logging OK
      this.proceedToSecondStep();
    }
  };

  resetStartingTimer = () => {
    if (this.startingTimer) {
      clearTimeout(this.startingTimer);
      this.startingTimer = null;
    }
  };

  handleToggleDebugPanelHotKey = (event: SyntheticKeyboardEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();

    this.setState(
      produce((draft) => {
        draft.isDebugPanelVisible = !draft.isDebugPanelVisible;
      }),
    );
  };

  onAnimationComplete = () => {
    this.isAnimationFinished = true;
    this.checkInitializationSequenceEnd();
  };

  checkInitializationSequenceEnd = () => {
    const { applicationName, successCallback, versionBO, versionCrmBack, versionCrmFront, versionShort } = this.props;
    const { durations, killedStreamCount, queryStatuses } = this.state;
    const { animation, areQueriesFinished, initializationSequenceEnd, initializationSequenceStart, isAnimationFinished, mainLayout, orderedSteps } = this;

    const isAppHidden = isTabHidden();
    const isReduceMotionActive = ReducedMotionManager.isReduced();

    if (areQueriesFinished && (isAnimationFinished || isAppHidden)) {
      const duration = new Intl.NumberFormat('fr-FR', {
        maximumFractionDigits: 3,
        minimumFractionDigits: 3,
        style: 'unit',
        unit: 'second',
      }).format((initializationSequenceEnd - initializationSequenceStart) / MILLISECONDS_PER_SECOND);
      logInfo('#==========================================#');
      logInfo(`║ ${applicationName.padEnd(Padding.Name)} ║`);
      logInfo('║                                          ║');
      logInfo(`║ Version: ${versionShort.padEnd(Padding.Version)} ║`);

      if (versionBO !== '') {
        logInfo(`║ Version BO: ${versionBO.padEnd(Padding.VersionBO)} ║`);
      }

      if (versionCrmBack !== '') {
        logInfo(`║ Version CRM Back: ${versionCrmBack.padEnd(Padding.VersionCrmBack)} ║`);
      }

      if (versionCrmFront !== '') {
        logInfo(`║ Version CRM Front: ${versionCrmFront.padEnd(Padding.VersionCrmFront)} ║`);
      }

      logInfo(`║ Initialization sequence: ${duration.padStart(Padding.InitSequence)} ║`);
      logInfo('#==========================================#');

      orderedSteps.forEach((step) => {
        const { displayName, name } = step;
        const { [name]: stepStatus } = queryStatuses;
        const { [name]: stepDuration } = durations;
        const statusStr = stepStatus === QueryStatus.Success ? 'OK' : 'KO';
        const css = stepStatus === QueryStatus.Success ? Css.StepSuccess : Css.StepError;
        const localDisplayName = StepName.VideofuturStopOpenStreams ? displayName.replace('<count>', killedStreamCount) : displayName;
        logDebug(`${localDisplayName.padEnd(Padding.StepName)} %c ${statusStr} %c ${stepDuration?.padStart(Padding.Duration) ?? ''}`, css, Css.Initial);
      });

      if (mainLayout) {
        mainLayout.classList.add('hidden');
        mainLayout.classList.remove('visible');

        if (isAppHidden || isReduceMotionActive) {
          // Hard transition since animation is not visible anyway
          animation?.destroy();
          this.animation = null;
          successCallback();
        } else {
          // Smooth transition
          mainLayout.addEventListener('animationend', successCallback, { passive: true });
        }
      } else {
        // Not suppose to happen
        successCallback();
      }
    }
  };

  sendLoginRequest = (authDeviceUrl: string, upgradeDeviceUrl: string, applicationId: string, subscriberId: string, deviceKey: string) => {
    const { localSendLoginRequest, unrecoverableFailureCallback } = this.props;

    this.setState(
      produce((draft) => {
        draft.queryStatuses[StepName.Login] = QueryStatus.InProgress;
      }),
    );

    const requestStart = performance.now();

    localSendLoginRequest(authDeviceUrl, upgradeDeviceUrl, applicationId, subscriberId, deviceKey)
      .then((result?: NETGEM_API_DMS_REQUEST_RESPONSE_CHALLENGE) => {
        if (result === undefined || result.status !== (DmsNetworkCode.Created as number)) {
          throw new Error('DMS authentication error');
        }

        const requestEnd = performance.now();
        this.setState(
          produce((draft) => {
            draft.durations[StepName.Login] = formatMs(requestEnd - requestStart);
          }),
        );
      })
      .catch((error: CustomNetworkError | Error) => {
        this.setState(
          produce((draft) => {
            draft.queryStatuses[StepName.Login] = QueryStatus.Error;
          }),
        );

        logError(`Login error: ${error.message}`);

        if (error instanceof CustomNetworkError) {
          logError(error.networkError);
          const customStatus = error.getCustomStatus();
          if (
            customStatus !== (DmsNetworkCode.MissingOrInvalidAuthenticationToken as number) &&
            // Unknown device
            customStatus !== (DmsNetworkCode.UnknownDevice as number) &&
            // Invalid device
            customStatus !== (DmsNetworkCode.InvalidDevice as number) &&
            // DMS did not provide correct error code
            customStatus !== (DmsNetworkCode.UnknownError as number)
          ) {
            // The reasons above cause the display of the login page
            unrecoverableFailureCallback();
          }
        }
      });
  };

  proceedToSecondStep = () => {
    this.setState(
      produce((draft) => {
        draft.currentSteps = this.rootStep.then ?? [];
        draft.queryStatuses[StepName.Login] = QueryStatus.Success;
        draft.nextSteps = this.rootStep.thenAfter ?? [];
      }),
      this.processCurrentSteps,
    );
  };

  processCurrentSteps = () => {
    const { enabledSteps } = this;
    const {
      currentSteps,
      currentSteps: { length: stepCount },
      nextSteps,
      nextSteps: { length: nextStepCount },
    } = this.state;

    if (stepCount === 0) {
      if (nextStepCount > 0) {
        // Proceed to second pass steps
        this.setState(
          produce((draft) => {
            draft.currentSteps = nextSteps;
            draft.nextSteps = [];
          }),
          this.processCurrentSteps,
        );
      } else {
        // All done
        this.resetStartingTimer();
        this.initializationSequenceEnd = performance.now();
        this.areQueriesFinished = true;
        this.checkInitializationSequenceEnd();
      }
      return;
    }

    const disabledSteps = [];
    let hasStartedProcess = false;

    // Process current steps not started yet
    for (let i = 0; i < stepCount; i++) {
      const { [i]: step } = currentSteps;
      const { name } = step;
      const {
        queryStatuses: { [name]: stepStatus },
      } = this.state;

      if (stepStatus === QueryStatus.NotStarted && stepStatus !== QueryStatus.Disabled && !this.startedSteps.has(name)) {
        if (enabledSteps.has(name)) {
          // Step is enabled
          hasStartedProcess = true;
          this.startedSteps.add(name);
          this.setState(
            produce((draft) => {
              draft.queryStatuses[name] = QueryStatus.InProgress;
            }),
            () => this.processStep(step),
          );
        } else {
          // Step is disabled
          disabledSteps.push(step);
        }
      }
    }

    if (disabledSteps.length > 0) {
      this.setState(
        produce((draft) => {
          draft.currentSteps = filterCurrentSteps(draft.currentSteps, disabledSteps);
          disabledSteps.forEach(({ name }) => (draft.queryStatuses[name] = QueryStatus.Disabled));
        }),
        () => {
          if (!hasStartedProcess) {
            // All current processes have been disabled, process next ones
            this.processCurrentSteps();
          }
        },
      );
    }
  };

  processStep = (step: STEP_ITEM) => {
    const {
      abortController: { signal },
      props,
    } = this;
    const { name, parameters, request } = step;

    if (!request) {
      return;
    }

    const args = parameters?.map((p) => props[p]) ?? [];
    const requestStart = performance.now();

    request(...args, signal)
      .then((response) => {
        const requestEnd = performance.now();

        if (name === StepName.VideofuturStopOpenStreams) {
          this.setState({ killedStreamCount: response.toString() });
        }

        this.setState(
          produce((draft) => {
            draft.currentSteps = filterCurrentSteps(draft.currentSteps, step);
            draft.durations[name] = formatMs(requestEnd - requestStart);
            draft.queryStatuses[name] = QueryStatus.Success;
            draft.stepProgress += 1;
          }),
          this.processCurrentSteps,
        );
      })
      .catch((error) => this.handleStepError(error, step));
  };

  handleStepError = (error: Error, step: STEP_ITEM) => {
    const { failureCallback, isRegisteredAsGuest } = this.props;
    const { abortController } = this;
    const { name } = step;

    if (error instanceof DOMException && error.name === 'AbortError') {
      // Error should be ignored since everything has already been taken care of
      return;
    }

    let message: ?string = null;

    if (error instanceof CustomNetworkError) {
      const { networkError } = error;
      if (networkError) {
        ({ message } = networkError);
      }
    }

    this.setState(
      produce((draft) => {
        draft.queryStatuses[name] = QueryStatus.Error;
      }),
    );

    const {
      [name]: { isOptional },
    } = STEPS;
    const logger = isOptional ? logWarning : logError;

    logger(`Error in step "${name}": ${message ?? 'no message'}`);
    logger(error);

    if (isOptional) {
      // Step failed but is optional: process remaining steps
      this.setState(
        produce((draft) => {
          draft.currentSteps = filterCurrentSteps(draft.currentSteps, step);
          draft.stepProgress += 1;
        }),
        this.processCurrentSteps,
      );
    } else {
      // Step failed and is not optional: stop everything
      abortController.abort('Unrecoverable error in StartView');
    }

    if (isRegisteredAsGuest || name === StepName.DeviceSettings || name === StepName.VideofuturAuthentication) {
      // Error in a mandatory step while in guest mode means something very bad: bail out and display login page to avoid infinite reload
      failureCallback();
    }
  };

  handleVersionOnClick = (event: SyntheticMouseEvent<HTMLElement> | SyntheticTouchEvent<HTMLElement>) => {
    const { localToggleDebugMode } = this.props;

    if ((event.ctrlKey || event.altKey) && event.shiftKey) {
      localToggleDebugMode();
    }
  };

  renderDebugPanel = (): React.Node => {
    const { isDebugModeEnabled, versionShort: version } = this.props;
    const { durations, isDebugPanelVisible, killedStreamCount, queryStatuses } = this.state;
    const { orderedSteps } = this;

    if (!orderedSteps) {
      // Startup tree not built yet
      return null;
    }

    return (
      <div aria-hidden className={clsx('debugContainer', isDebugPanelVisible === true && 'debugSlideIn', isDebugPanelVisible === false && 'debugSlideOut')}>
        <div aria-hidden className='version' onClick={this.handleVersionOnClick}>
          <div>Version {version}</div>
          <DebugPicto isEnabled={isDebugModeEnabled} />
        </div>
        {orderedSteps.map((step) => {
          const { displayName, name } = step;
          const { [name]: stepStatus } = queryStatuses;
          const { [name]: duration } = durations;

          return (
            <div className={clsx('line', stepStatus)} key={name}>
              <div className='label'>{name === StepName.VideofuturStopOpenStreams ? displayName.replace('<count>', killedStreamCount) : displayName}</div>
              <div className='duration'>{duration}</div>
              <div className='status'>{stepStatus === QueryStatus.InProgress ? <InfiniteCircleLoaderArc className='queryLoader' /> : null}</div>
            </div>
          );
        })}
      </div>
    );
  };

  render(): React.Node {
    const { areQueriesFinished, state, stepCount } = this;
    const { isDebugModeEnabled, isLoadingAnimationVisible, versionShort: version } = this.props;
    const { stepProgress } = state;

    const progress = stepProgress / stepCount;

    return (
      <div className='start'>
        <div
          className='mainLayout visible'
          ref={(instance: HTMLElement | null) => {
            this.mainLayout = instance;
          }}
        >
          <div aria-hidden className='loaderAnimation' />
          <div className='progressBar' style={{ transform: `scaleX(${progress})` }} />
          {this.renderDebugPanel()}
        </div>
        <div aria-hidden className='footerVersion' onClick={this.handleVersionOnClick}>
          <div>{Localizer.localize('authentication.version', { version })}</div>
          <DebugPicto isEnabled={isDebugModeEnabled} />
        </div>
        {!isLoadingAnimationVisible ? <InfiniteCircleLoaderArc className='startLoader' /> : null}
        <div aria-busy={!areQueriesFinished} aria-live='polite' className='screenReaderOnly'>
          {areQueriesFinished ? Localizer.localize('accessibility.navigation.application_loaded') : Localizer.localize('accessibility.navigation.application_loading')}
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state: CombinedReducers): ReduxStartReducerStateType => {
  return {
    applicationId: state.appRegistration.applicationId,
    applicationName: state.appConfiguration.applicationName,
    authDeviceUrl: state.appRegistration.authDeviceUrl,
    authenticationToken: state.appRegistration.authenticationToken,
    deviceKey: state.appRegistration.deviceKey,
    features: state.appConfiguration.features,
    isDebugModeEnabled: state.appConfiguration.isDebugModeEnabled,
    isRegisteredAsGuest: state.appRegistration.registration === RegistrationType.RegisteredAsGuest,
    subscriberId: state.appRegistration.subscriberId,
    upgradeDeviceUrl: state.appRegistration.upgradeDeviceUrl,
    versionBO: state.appConfiguration.versionBO,
    versionCrmBack: state.appConfiguration.versionCrmBack,
    versionCrmFront: state.appConfiguration.versionCrmFront,
    versionShort: state.appConfiguration.versionAppShort,
  };
};

const mapDispatchToProps = (dispatch: Dispatch): ReduxStartDispatchToPropsType => {
  return {
    localCreateTagListAliases: (signal: AbortSignal): Promise<NETGEM_API_V8_REQUEST_RESPONSE | null> => dispatch(createTagListAliases(signal)),
    localSendCommercialOfferRequest: (signal: AbortSignal): Promise<void> => dispatch(sendCommercialOffersRequest(signal)),
    localSendContinuousWatchingHubRequest: (signal: AbortSignal): Promise<void> => dispatch(sendV8ContinuousWatchingHubRequest(signal)),
    localSendDefaultHubRequest: (signal: AbortSignal): Promise<void> => dispatch(sendV8DefaultHubRequest(signal)),
    localSendDefaultRightsRequest: (signal: AbortSignal): Promise<void> => dispatch(sendDefaultRightsRequest(signal)),
    localSendDiscoveryRequest: (signal: AbortSignal): Promise<void> => dispatch(sendV8DiscoveryRequest(signal)),
    localSendGetDeviceChannelListRequest: (signal: AbortSignal): Promise<void> => dispatch(sendGetDeviceChannelListRequest(signal)),
    localSendGetDeviceSettingRequest: (signal: AbortSignal): Promise<void> => dispatch(sendGetDeviceSettingsRequest(signal)),
    localSendHubRequest: (signal: AbortSignal): Promise<void> => dispatch(sendV8HubRequest(null, signal)),
    localSendListAliasPostRequest: (signal: AbortSignal): Promise<void> => dispatch(sendV8ListAliasPostRequest(null, signal)),
    localSendLoginRequest: (
      authDeviceUrl: string,
      upgradeDeviceUrl: string,
      applicationId: string,
      subscriberId: string,
      deviceKey: string,
      signal?: AbortSignal,
    ): Promise<NETGEM_API_DMS_REQUEST_RESPONSE_CHALLENGE> => dispatch(sendLoginRequest(authDeviceUrl, upgradeDeviceUrl, applicationId, subscriberId, deviceKey, null, null, true, signal)),
    localSendMetadataChannelsRequest: (signal: AbortSignal): Promise<NETGEM_API_V8_REQUEST_RESPONSE | null> => dispatch(sendV8MetadataChannelsRequest(signal)),
    localSendUserRightsRequest: (signal: AbortSignal): Promise<void> => dispatch(sendUserRightsRequest(signal)),
    localSendVideofuturAuthenticationRequest: (applicationId: string, deviceKey: string, signal: AbortSignal): Promise<void> =>
      dispatch(sendVideofuturAuthenticationRequest(applicationId, deviceKey, signal)),
    localSendVideofuturDiscoveryRequest: (signal: AbortSignal): Promise<void> => dispatch(sendVideofuturDiscoveryRequest(signal)),
    localSendVideofuturPersonalDataRequest: (signal: AbortSignal): Promise<void> => dispatch(sendVideofuturPersonalDataRequest(signal)),
    localStopVideofuturOpenStreams: (signal: AbortSignal): Promise<number> => dispatch(stopOpenStreams(null, signal)),
    localToggleDebugMode: (): void => dispatch(toggleDebugMode()),
  };
};

const Start: React.ComponentType<StartPropType> = connect(mapStateToProps, mapDispatchToProps)(StartView);

export default Start;
